/**
 * neli-market-fix.js
 * -------------------------------------------------------------------------
 * Fix: the Neli pre-order button checkout ignores the buyer's Shopify Markets
 *      selection. It creates the checkout in the store's default market
 *      (HK / HKD), which for B2C shows the wrong currency and for B2B (which is
 *      locked to its own EUR market) makes the checkout invalid -> bounce to
 *      the home page.
 *
 * HOW NELI WORKS
 *   On click, neli-preorder.js POSTs to its own app proxy:
 *       POST /apps/pre-order?action=checkout  { variantId, quantity, sellingPlanId }
 *       -> Neli's SERVER builds a checkout and returns { checkoutUrl }
 *       -> window.location.assign(checkoutUrl)
 *   That server checkout ignores the buyer's market, so it can't be fixed by
 *   rewriting the redirect URL (the market is baked in at creation).
 *
 * WHAT THIS DOES (same for B2C and B2B)
 *   Monkey-patch window.fetch. When we see the Neli checkout call, instead of
 *   letting it reach Neli's server we add the item to the NATIVE Shopify cart
 *   with a "Pre-order" line-item property and hand Neli's own code
 *   { checkoutUrl: "/checkout" } so it goes to the standard checkout.
 *
 *   We do NOT add the selling plan:
 *     - the native cart keeps the buyer's selected market/currency, so the
 *       checkout is correct for B2C and valid for B2B (no bounce);
 *     - Shopify's storefront cart rejects selling plans for B2B anyway
 *       ("Only one-time purchase is available for B2B orders"), so a
 *       selling-plan add can't be uniform;
 *     - pre-orders are backorderable products (Continue selling when out of
 *       stock) and the 20% deposit is handled manually, so the "Pre-order"
 *       property is enough to identify the order. The product's release date /
 *       Pre-orders collection also mark it.
 *
 *   Every other fetch on the page is left untouched.
 *
 * CAVEATS
 *   - Bypasses Neli's own server checkout; orders are identified by the
 *     "Pre-order" property (not a selling-plan line). Re-test after any change.
 *   - Checks out the whole cart (not just the pre-order item).
 *
 * REMOVE
 *   Delete this file and its <script> include in layout/theme.liquid.
 *   Long-term proper fix is on Neli's side (respect Shopify Markets).
 *
 * Added 2026-07-24.
 * -------------------------------------------------------------------------
 */
(function () {
  "use strict";

  if (window.__neliMarketFixInstalled) return;
  var origFetch = window.fetch;
  if (typeof origFetch !== "function" || typeof Response === "undefined") return;
  window.__neliMarketFixInstalled = true;

  function jsonResponse(obj, status) {
    return new Response(JSON.stringify(obj), {
      status: status || 200,
      headers: { "Content-Type": "application/json" }
    });
  }

  window.fetch = function (input, init) {
    var url, method;
    try {
      url = typeof input === "string" ? input : (input && input.url) || "";
      method = ((init && init.method) || (input && input.method) || "GET").toUpperCase();
    } catch (e) {
      return origFetch.apply(this, arguments);
    }

    var isNeliCheckout =
      url.indexOf("/apps/pre-order") !== -1 &&
      url.indexOf("action=checkout") !== -1 &&
      method === "POST";

    if (!isNeliCheckout) {
      return origFetch.apply(this, arguments);
    }

    // Parse Neli's payload: { variantId, quantity, sellingPlanId }
    var payload = {};
    try { payload = JSON.parse((init && init.body) || (input && input.body) || "{}"); } catch (e) { payload = {}; }

    var variantId = String(payload.variantId || "").replace(/[^0-9]/g, "");
    var quantity = Number(payload.quantity) || 1;

    if (!variantId) {
      return origFetch.apply(this, arguments); // nothing we can do; let Neli handle it
    }

    // Add to the native cart (keeps market/currency) as a one-time purchase with
    // a "Pre-order" marker, then send Neli to the standard checkout.
    return origFetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: { "Pre-order": "Yes" }
      })
    }).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (t) {
          return jsonResponse({ error: "cart add failed: " + t }, res.status || 400);
        });
      }
      return jsonResponse({ checkoutUrl: "/checkout" }, 200);
    }).catch(function (e) {
      if (window.console && console.warn) console.warn("[neli-market-fix] cart add error:", e);
      return jsonResponse({ error: "cart add error" }, 500);
    });
  };
})();
